import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Button from '@components/button/Button'
import Tabs from '@components/tabs/Tabs'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'

import ControleDetailScreen from './ControleDetailScreen'

import Presta from './list/Presta'
import Manif from './list/Manif'
import Space from './list/Space'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'

import { AuthScope } from '@api/enum'

import { createEvent, createStore } from 'effector'

const TABS = ['presta', 'manif', 'space'] as const

const setTab = createEvent<(typeof TABS)[number]>('setControleListTab')

const TabStore = createStore<(typeof TABS)[number]>('presta', { name: 'controles_list_tab' }).on(
  setTab,
  (s, payload) => payload
)

const ControleListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const user = useReducer(UserStore.store, (s) => s.user)
  const tab = useReducer(TabStore, (s) => s)

  const startControleButton: HeaderButton = {
    label: i18n.t('screens.controles.start'),
    onClick: () => navigation.push('/controles/form/start'),
  }

  const canCreateControle =
    scopes.includes(AuthScope.ControleQualiteSiteCreation) ||
    scopes.includes(AuthScope.ControleQualitePrestataireCreation)

  const buttons = canCreateControle ? [startControleButton] : []

  const renderBottom = () => {
    if (!isPhone || !canCreateControle) {
      return null
    }

    return (
      <Footer>
        <Button label={startControleButton.label} onClick={startControleButton.onClick} status="active" />
      </Footer>
    )
  }

  const options = TABS.map((t) => ({ label: i18n.t(`screens.controles.tabs.${t}`), value: t }))
  const option = options.find((opt) => opt.value === tab)

  if (user && user.societeId) {
    return <ControleDetailScreen societeId={user.societeId} />
  }

  return (
    <Root
      header={isPhone ? i18n.t('screens.controles.title') : undefined}
      fixed
      onLeft={navigation.goBack}
      renderFixedBottom={renderBottom}>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.controles.title')} buttons={buttons} />
        </HeaderContainer>
      )}
      <TabsContainer>
        {isPhone ? (
          <SegmentedControl data={options} onItemSelected={(opt) => setTab(opt.value)} value={option} />
        ) : (
          <Tabs data={options} value={option} onItemSelected={(opt) => setTab(opt.value)} isBig />
        )}
      </TabsContainer>

      {tab === 'presta' ? <Presta /> : tab === 'manif' ? <Manif /> : <Space />}
    </Root>
  )
}

export default ControleListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const Footer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`

const TabsContainer = styled(View)`
  justify-content: flex-start;
  padding: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`
